import { motion } from 'framer-motion';

/**
 * 3D DNA double helix — two intertwined strands with base-pair rungs, rolling on Y axis.
 * Each node pair is positioned using sin/cos to create the helical twist.
 */
function MiniHelix() {
  const rungs = 12;
  const height = 72;
  const radius = 14;

  const nodes = Array.from({ length: rungs }).map((_, i) => {
    const t = i / (rungs - 1);
    const angle = t * Math.PI * 3; // 1.5 full twists
    const y = t * height;
    const x1 = Math.cos(angle) * radius;
    const z1 = Math.sin(angle) * radius;
    const x2 = Math.cos(angle + Math.PI) * radius;
    const z2 = Math.sin(angle + Math.PI) * radius;
    return { y, x1, z1, x2, z2 };
  });

  return (
    <div
      className="relative"
      style={{ width: 40, height: 80, perspective: 300 }}
    >
      <motion.div
        className="absolute inset-0"
        style={{ transformStyle: 'preserve-3d' }}
        animate={{ rotateY: 360 }}
        transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
      >
        {nodes.map((n, i) => (
          <div key={i} className="absolute" style={{ transformStyle: 'preserve-3d' }}>
            {/* Strand A node */}
            <div
              className="absolute rounded-full"
              style={{
                width: 5,
                height: 5,
                left: 20 + n.x1 - 2.5,
                top: 4 + n.y - 2.5,
                transform: `translateZ(${n.z1}px)`,
                background: '#2dd4a0',
                boxShadow: '0 0 8px #2dd4a066',
              }}
            />
            {/* Strand B node */}
            <div
              className="absolute rounded-full"
              style={{
                width: 5,
                height: 5,
                left: 20 + n.x2 - 2.5,
                top: 4 + n.y - 2.5,
                transform: `translateZ(${n.z2}px)`,
                background: '#38bdf8',
                boxShadow: '0 0 8px #38bdf866',
              }}
            />
            {/* Base-pair rung connecting the two strands */}
            {i % 2 === 0 && (
              <div
                className="absolute"
                style={{
                  top: 4 + n.y,
                  left: 20 + Math.min(n.x1, n.x2),
                  width: Math.abs(n.x1 - n.x2),
                  height: 1,
                  background: 'rgba(255,255,255,0.1)',
                  transform: `translateZ(${(n.z1 + n.z2) / 2}px)`,
                }}
              />
            )}
            {/* Strand connector (backbone) */}
            {i < nodes.length - 1 && (
              <>
                <div
                  className="absolute"
                  style={{
                    left: 20 + n.x1 - 0.5,
                    top: 4 + n.y,
                    width: 1,
                    height: height / (rungs - 1) + 1,
                    background: 'linear-gradient(to bottom, #2dd4a044, #2dd4a022)',
                    transform: `translateZ(${n.z1}px)`,
                  }}
                />
                <div
                  className="absolute"
                  style={{
                    left: 20 + n.x2 - 0.5,
                    top: 4 + n.y,
                    width: 1,
                    height: height / (rungs - 1) + 1,
                    background: 'linear-gradient(to bottom, #38bdf844, #38bdf822)',
                    transform: `translateZ(${n.z2}px)`,
                  }}
                />
              </>
            )}
          </div>
        ))}
      </motion.div>
    </div>
  );
}

export function LoadingScreen() {
  return (
    <div className="flex h-full min-h-[300px] flex-col items-center justify-center gap-6">
      <MiniHelix />

      {/* Progress bar */}
      <div className="h-0.5 w-40 overflow-hidden rounded-full bg-white/[0.06]">
        <motion.div
          className="h-full rounded-full bg-gradient-to-r from-[hsl(162,72%,46%)] to-[hsl(190,90%,55%)]"
          animate={{ width: ['0%', '100%', '0%'] }}
          transition={{ duration: 2.2, repeat: Infinity, ease: 'easeInOut' }}
        />
      </div>
    </div>
  );
}
